// An active order's KOTs on the Orders screen: list, print / reprint, and
// telling the server which KOTs this device printed.
//
// The rule (owner, 2026-10-29): a KOT says REPRINT only when it was printed
// before, by any device. The server counts prints (kots.print_count):
// POST /kot/{id}/print counts its own print; the KOTs a send (POST /confirm,
// QR accept) hands out are counted when the device that printed them reports
// them to POST /kot/printed.
import { api } from "@/lib/api";
import type { KOTRouting } from "@/types/outlets";

export interface OrderKotLine {
  id: number;
  item_name: string;
  /** Current quantity ("2.000"; 0 once removed). */
  quantity: string;
  /** sent, preparing, ready, served or cancelled. */
  status: string;
}

/** GET /orders/{id}/kots: one KOT of the order. */
export interface OrderKot {
  id: string;
  kot_number: string;
  status: "open" | "ready" | "served" | "cancelled" | string;
  created_at: string;
  station: { id: string; station_name: string; printer_id?: string | null } | null;
  items: OrderKotLine[];
  /** Prints the server counted (0 also when prints were never reported). */
  print_count: number;
  last_printed_at: string | null;
  /**
   * printed (a print was counted), not_printed (evidence: the device that sent
   * it asked a till to print it), unknown (no evidence either way: it may well
   * have printed, e.g. sent from Android before it reported prints).
   */
  print_state?: "printed" | "not_printed" | "unknown" | null;
  /** The latest remote print request for it: pending, claimed, printed, failed, expired. */
  print_request_status?: string | null;
}

export interface OrderKotsResponse {
  kots: OrderKot[];
  total: number;
  /** The outlet's KOT routing (readable without the business-settings permission). */
  kot_routing: KOTRouting;
}

/** POST /kot/{id}/print. The print is counted already (never report it to /kot/printed). */
export interface KotPrintResponse {
  kot_id: string;
  kot_number: string;
  /** Base64 ESC/POS. */
  escpos_data: string;
  paper_width: number;
  station_id: string | null;
  station_name: string | null;
  /** The KOT's station printer; null: this device's default KOT printer. */
  printer_id: string | null;
  copies: number;
  print_count: number;
  is_reprint: boolean;
  /** POST /kot/{id}/print/undo with it when the print service did not take the job. */
  undo_token?: string | null;
}

/** KOTs print on paper with this routing (kds: the kitchen display only). */
export function kotRoutingPrints(routing: KOTRouting | null | undefined): boolean {
  return routing === "printer" || routing === "both";
}

/** "KOT 12" for "ORD-1043-KOT-12" (the number the slip and the cart strip show). */
export function kotLabel(kotNumber: string): string {
  const marker = "-KOT-";
  const at = kotNumber.lastIndexOf(marker);
  return `KOT ${at === -1 ? kotNumber : kotNumber.slice(at + marker.length)}`;
}

/** A till is printing it (or about to): the remote request is open. */
export function waitingForTill(kot: Pick<OrderKot, "print_request_status">): boolean {
  return kot.print_request_status === "pending" || kot.print_request_status === "claimed";
}

/** A print of it was recorded: the next one is a reprint. */
export function printedBefore(kot: Pick<OrderKot, "print_state" | "print_count">): boolean {
  return kot.print_state === "printed" || kot.print_count > 0;
}

/**
 * "Printed 2×", "Not printed" (only with evidence it did not), "Sent to a
 * till", or nothing when nobody knows (it may well have printed).
 */
export function kotPrintLabel(kot: Pick<OrderKot, "print_state" | "print_count" | "print_request_status">): string {
  if (printedBefore(kot)) return `Printed ${Math.max(kot.print_count, 1)}×`;
  if (kot.print_state === "not_printed") return waitingForTill(kot) ? "Sent to a till" : "Not printed";
  return "";
}

/** The KOT's live lines: "2× Paneer Tikka, 1× Lassi" (removed lines left off, as on the slip). */
export function kotItemSummary(kot: Pick<OrderKot, "items">): string {
  return kot.items
    .filter((line) => line.status !== "cancelled")
    .map((line) => `${formatQuantity(line.quantity)}× ${line.item_name}`)
    .join(", ");
}

function formatQuantity(quantity: string): string {
  const n = Number.parseFloat(quantity);
  return Number.isFinite(n) ? String(Number(n.toFixed(3))) : quantity;
}

/** A KOT that can be printed from the till (every item removed: nothing to print). */
export function canPrintKot(kot: Pick<OrderKot, "status">): boolean {
  return kot.status !== "cancelled";
}

/**
 * KOTs known not to have printed (printable, no till on it right now). A KOT
 * whose printing is unknown is never counted here: it may well have printed,
 * and a second "original" could be cooked twice.
 */
export function unprintedKots(kots: OrderKot[]): OrderKot[] {
  return kots.filter(
    (kot) => canPrintKot(kot) && kot.print_state === "not_printed" && kot.print_count === 0 && !waitingForTill(kot),
  );
}

export function fetchOrderKots(orderId: string): Promise<OrderKotsResponse> {
  return api.get<OrderKotsResponse>(`/api/v1/orders/${orderId}/kots`);
}

export function printKot(kotId: string): Promise<KotPrintResponse> {
  return api.post<KotPrintResponse>(`/api/v1/kot/${kotId}/print`, {});
}

/** The print service did not take that print: take it back off the count. Never throws. */
export async function undoKotPrint(kotId: string, undoToken: string | null | undefined): Promise<void> {
  if (!undoToken) return;
  try {
    await api.post(`/api/v1/kot/${kotId}/print/undo`, { undo_token: undoToken });
  } catch (error) {
    console.warn("Could not take back a KOT print", error);
  }
}

/**
 * Tell the server this device printed these KOTs from a send's ESC/POS (the
 * print service took the job), so their next print anywhere says REPRINT.
 * Best effort: never throws, and sends nothing for an empty list.
 */
export async function reportPrintedKots(kotIds: Array<string | null | undefined>): Promise<void> {
  const ids = [...new Set(kotIds.filter((id): id is string => !!id))];
  if (ids.length === 0) return;
  try {
    await api.post("/api/v1/kot/printed", { kot_ids: ids });
  } catch (error) {
    // The KOT printed; only its "Printed" count is behind (the next till print says KOT, not REPRINT).
    console.warn("Could not record printed KOTs", error);
  }
}
